"use client"

import { ArrowRight, CheckCircle2 } from 'lucide-react'

import { getAngiAffiliateUrl } from '@/data/angi-affiliate'
import type { ServiceQuoteFunnel } from '@/data/service-quote-funnels'
import { trackEvent } from '@/lib/analytics'
import { useServiceLeadCtaViewed } from '@/hooks/use-service-lead-cta-viewed'

interface AngiReferralCardProps {
  funnel: ServiceQuoteFunnel
}

/**
 * Right-column referral card on the /…-quotes landing pages. Replaced the
 * 3-step first-party lead form in the 2026-07-18 Angi pivot: with no
 * fulfillment plan, quote intent routes straight to Angi's request funnel
 * through the CJ-tracked link instead of collecting contact details we
 * cannot act on.
 */
export function AngiReferralCard({ funnel }: AngiReferralCardProps) {
  const angiUrl = getAngiAffiliateUrl(funnel.serviceType, 'quote_landing_page', funnel.path)
  const viewRef = useServiceLeadCtaViewed({
    service_type: funnel.serviceType,
    source_page: funnel.path,
    source_placement: 'quote_landing_page',
  })

  return (
    <div
      ref={viewRef}
      className="w-full rounded-lg border border-gray-200 bg-white p-6 text-gray-950 shadow-sm sm:p-7"
    >
      <p className="text-xs font-semibold uppercase tracking-[0.12em] text-green-700">
        Free local quotes
      </p>
      <h2 className="mt-1 text-xl font-semibold text-gray-950">
        Get matched with {funnel.label.toLowerCase()} pros near you
      </h2>
      <p className="mt-2 text-sm leading-6 text-gray-600">
        We partner with Angi to connect you with local pros. Share your project
        there and compare free quotes, usually within a day.
      </p>
      <ul className="mt-4 space-y-2">
        {['Free to request, no obligation', 'Local, background-checked pros', 'Compare multiple quotes'].map((item) => (
          <li key={item} className="flex items-start gap-2 text-sm leading-6 text-gray-700">
            <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-green-600" aria-hidden="true" />
            {item}
          </li>
        ))}
      </ul>
      <a
        href={angiUrl}
        target="_blank"
        rel="sponsored noopener noreferrer"
        className="mt-5 inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-md bg-green-700 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-green-800 active:scale-[0.98]"
        onClick={() =>
          trackEvent('service_lead_angi_clicked', {
            service_type: funnel.serviceType,
            source_page: funnel.path,
            source_placement: 'quote_landing_page',
            placement: 'quote_landing_page',
          })
        }
      >
        {funnel.ctaLabel}
        <ArrowRight className="h-4 w-4" aria-hidden="true" />
      </a>
      <p className="mt-3 text-xs leading-5 text-gray-500">
        Angi is a partner; we may earn a commission if you request quotes there.
        Your request goes directly to Angi, not to What Grass Is This?.
      </p>
    </div>
  )
}
